/* eslint-disable max-lines */
import { numberToPrettyString, parseNumber } from '@cian/utils/lib/shared/number_helpers';
import { plural } from '@cian/utils/lib/shared/plural';
import { isNil } from 'ramda';

import {
  IAgentBonusSchema,
  IBuildingSchema,
  ICoworking,
  IOfferSchema,
  IParkingSchema,
  IPriceSchema,
  TContractType,
  TIncludedOption,
  TLeaseType,
  TVatType,
} from 'shared/types/api-models/offer-card/v1/get-offer-data';

import { FDealType, getDealType } from './deal_type';
import { CurrencySymbol } from './formatters';
import { getIncludedOrNotIncludedText } from './getIncludedOrNotIncludedText';

export function vatTypeFormatter(
  type: TVatType | null,
  totalVat: number | null,
  minTotalVat: number | null,
  /**
   * @todo Удалить строку с RS
   * @description Данный функционал появился в задаче CD-143094, будет удалён в задаче CD-143198
   */
  isCommercialNewVatTextEnabled?: boolean,
) {
  switch (type) {
    case 'usn':
      return 'УСН';
    case 'included':
      return vatFormat(totalVat, minTotalVat, isCommercialNewVatTextEnabled);
    default:
      /**
       * @todo Удалить строку с RS и оставить только "НДС не облагается"
       * @description Данный функционал появился в задаче CD-143094, будет удалён в задаче CD-143198
       */
      return isCommercialNewVatTextEnabled ? 'НДС не облагается' : 'Без НДС';
  }
}

export function vatFormat(
  totalVat: number | null,
  minTotalVat: number | null,
  isCommercialNewVatTypeWordingEnabled?: boolean,
) {
  const nbsp = '\u00a0';

  if (totalVat && !minTotalVat) {
    return [`НДС включен:`, `${prettify(totalVat)}${nbsp}${CurrencySymbol.Rur}`];
  }

  if (minTotalVat && totalVat) {
    return [`НДС включен:`, `${prettify(minTotalVat)}-${prettify(totalVat)}${nbsp}${CurrencySymbol.Rur}`];
  }

  return isCommercialNewVatTypeWordingEnabled ? 'НДС не облагается' : 'Без НДС';
}

export function leaseTypeFormatter(lease: TLeaseType | null): string {
  switch (lease) {
    case 'direct':
      return 'Прямая Аренда';
    case 'sublease':
      return 'Субаренда';
    default:
      return '-';
  }
}

export function leaseTermTypeFormatter(leasetTerm: 'longTerm' | 'fewMonths' | null): string {
  switch (leasetTerm) {
    case 'fewMonths':
      return 'Несколько Месяцев';
    case 'longTerm':
      return 'Длительный';
    default:
      return '-';
  }
}

export function hasGracePeriodFormatter(gracePeriod: boolean | null): string {
  switch (gracePeriod) {
    case true:
      return 'Да';
    case false:
      return 'Нет';
    default:
      return '-';
  }
}

export function parkingFormatter(parking: IParkingSchema | null): string {
  if (!parking) {
    return '-';
  }

  if (parking.isFree) {
    return 'Бесплатно';
  } else if (parking.pricesMonthly) {
    return `${prettify(parking.pricesMonthly.rur)} ${CurrencySymbol.Rur}/место`;
  }

  return '-';
}

function getPostfix(dealType: FDealType): string {
  switch (dealType) {
    case FDealType.RentLongterm:
      return '/мес';
    case FDealType.RentDaily:
      return '/сут';
    default:
      return '';
  }
}

export function formatPPA(contractType: TContractType | null) {
  if (contractType === 'leaseAssignment') {
    return 'ППА';
  }

  return 'Свободная продажа';
}

export function formatRentPrice(offer: IOfferSchema) {
  if (!isNil(offer.priceTotalPerMonthRur)) {
    if (!isNil(offer.minPriceTotalPerMonthRur)) {
      const fromPrice = prettify(offer.minPriceTotalPerMonthRur);
      const toPrice = prettify(offer.priceTotalPerMonthRur);

      return [`от ${fromPrice} `, `до ${toPrice} ${CurrencySymbol.Rur}/мес.`];
    }

    return `${prettify(offer.priceTotalPerMonthRur)} ${CurrencySymbol.Rur}/мес.`;
  }

  return `${prettify(offer.bargainTerms.prices.rur)} ${CurrencySymbol.Rur}/мес.`;
}

export function formatSalePrice(offer: IOfferSchema) {
  if (!isNil(offer.priceTotalRur)) {
    const toPrice = prettify(offer.priceTotalRur);
    if (!isNil(offer.minPriceTotalRur)) {
      const fromPrice = prettify(offer.minPriceTotalRur);

      return [`от ${fromPrice}`, `до ${toPrice} ${CurrencySymbol.Rur}`];
    }

    return `${toPrice} ${CurrencySymbol.Rur}`;
  }

  return `${prettify(offer.bargainTerms.prices.rur)} ${CurrencySymbol.Rur}`;
}

export function priceFormatter(offer: IOfferSchema): string | string[] {
  const dealType = getDealType(offer);
  const postfix = getPostfix(dealType);

  switch (true) {
    case !!(dealType & FDealType.Rent):
      return formatRentPrice(offer);
    case !!(dealType & FDealType.Sale):
      return formatSalePrice(offer);
    default:
      return `${prettify(offer.bargainTerms.prices.rur)} ${CurrencySymbol.Rur}/${postfix}.`;
  }
}

export function AreaPerMetterFormatter(pricePerUnitAreaPerYearRur: number | null, isAreaParts: boolean) {
  const squareSymbol = '\u00B2';
  const symbolFrom = isAreaParts ? 'от ' : '';

  return pricePerUnitAreaPerYearRur
    ? `${symbolFrom} ${prettify(pricePerUnitAreaPerYearRur)} ${CurrencySymbol.Rur} за м${squareSymbol}/год`
    : '-';
}

export function AreaPerUnitFormatter(pricePerUnitAreaRur: number | null, isAreaParts: boolean) {
  const squareSymbol = '\u00B2';
  const symbolFrom = isAreaParts ? 'от ' : '';

  return pricePerUnitAreaRur
    ? `${symbolFrom} ${prettify(pricePerUnitAreaRur)} ${CurrencySymbol.Rur} за м${squareSymbol}`
    : '-';
}

export function utilityCharges(includedOptions: TIncludedOption[] | null): string {
  const hasUtilityCharges = !!includedOptions?.includes('utilityCharges');

  return getIncludedOrNotIncludedText('plural')(hasUtilityCharges);
}

export function operationalCosts(includedOptions: TIncludedOption[] | null): string {
  const hasOperationalCosts = !!includedOptions?.includes('operationalCosts');

  if (!hasOperationalCosts) {
    return 'Не предусмотрены';
  }

  return getIncludedOrNotIncludedText('plural')(hasOperationalCosts);
}

export function prepayMonths(prepayMonth: number | null) {
  if (!prepayMonth) {
    return '-';
  }

  switch (true) {
    case prepayMonth <= 11:
      return `${prepayMonth} ${plural(prepayMonth, ['месяц', 'месяца', 'месяцев'])}`;
    case prepayMonth === 12:
      return '1 год';
    default:
      return '-';
  }
}

export function agentBonus(bonus: IAgentBonusSchema | null) {
  if (!bonus || !bonus.value) {
    return '-';
  }

  switch (bonus.paymentType) {
    case 'percent':
      return `${numberToPrettyString(bonus.value)}%`;
    case 'fixed':
      return `${prettify(bonus.value)} ${CurrencySymbol.Rur}`;
    default:
      return '';
  }
}

export function agentFee(fee: number | null) {
  if (!fee) {
    return '-';
  }

  return `${fee}%`;
}

export function clientFee(fee: number | null) {
  return fee ? `${fee}%` : '-';
}

export function securityDeposites(securityDepositPrices: IPriceSchema | null) {
  return securityDepositPrices ? `${prettify(securityDepositPrices.rur)} ${CurrencySymbol.Rur}` : '-';
}

export function prettify(a: number) {
  return numberToPrettyString(Math.round(a));
}

export function formatMinLeaseTerm(minLeaseTerm: number | null) {
  if (!minLeaseTerm) {
    return '-';
  }

  return `От ${minLeaseTerm} мес.`;
}

export function formatTotalArea(totalArea: string | null) {
  if (!totalArea) {
    return '-';
  }

  const { integer, fractional } = parseNumber(totalArea);
  if (!Number(fractional)) {
    return `${integer} м\u00B2`;
  }

  return `${integer},${fractional} м\u00B2`;
}

export function formatFloor(floorNumber: number, building: IBuildingSchema | null) {
  if (!(building && building.floorsCount) || !floorNumber) {
    return '-';
  }

  return `${floorNumber} из ${building.floorsCount}`;
}

export function formatPriceForWorkplace(priceForWorkplace: number | null | undefined) {
  if (!priceForWorkplace) {
    return '-';
  }

  return `${prettify(priceForWorkplace)} ${CurrencySymbol.Rur}/мес.`;
}

export function formatAccessTime(coworking: ICoworking | undefined) {
  if (!coworking || !coworking.accessType || (!coworking.workTime && !coworking.isRoundTheClock)) {
    return '-';
  }

  const { accessType, isRoundTheClock, workTime } = coworking;

  const accessTypeText = accessType === 'weekdays' ? 'пн — пт' : 'ежедневно';
  let workTimeText = '';

  if (!isRoundTheClock && workTime?.timeFrom && workTime?.timeTo) {
    workTimeText = `с ${workTime.timeFrom} до ${workTime.timeTo}`.replace(/\s/g, '\u00a0');
  }

  if (isRoundTheClock) {
    workTimeText = 'круглосуточно';
  }

  return `${accessTypeText} ${workTimeText}`;
}

export function formatOwnerDeposit(deposit: number) {
  return `${prettify(deposit)} ${CurrencySymbol.Rur}/мес.`;
}
